import React from 'react';

import { useIntl } from 'react-intl';

import Link from '@atlaskit/link/link';

import { getSplunkElsTraceUrl } from '../get-splunk-els-trace-url';
import { getSplunkIntentDetectionTraceUrl } from '../get-splunk-intent-detection-trace-url';
import { getSplunkTraceUrl } from '../get-splunk-trace-url';
import { messages } from '../messages';

type SplunkLinkProps = {
	traceId: string | null | undefined;
	timestamp?: string | number | null;
	/**
	 * Which Splunk query shape to use:
	 * - `'default'` (answer trace): `traceId="<id>"`
	 * - `'intentDetection'`: `` `micros_xp-knowledge-discovery` traceId = "<id>" ``
	 * - `'els'`: `` `micros_kserving-entity-linking` env=prod* <id> ``
	 */
	variant?: 'default' | 'intentDetection' | 'els';
	testId?: string;
};

const getSplunkUrlForVariant = (
	variant: 'default' | 'intentDetection' | 'els',
	traceId: string | null | undefined,
	timestamp: string | number | null | undefined,
): string | undefined => {
	switch (variant) {
		case 'intentDetection':
			return getSplunkIntentDetectionTraceUrl(traceId, timestamp);
		case 'els':
			return getSplunkElsTraceUrl(traceId, timestamp);
		default:
			return getSplunkTraceUrl(traceId, timestamp);
	}
};

/**
 * Renders a "Splunk" link that opens Splunk logs filtered to the given trace ID.
 * Renders nothing when no trace ID is available.
 */
export const SplunkLink = ({
	traceId,
	timestamp,
	variant = 'default',
	testId,
}: SplunkLinkProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const splunkUrl = getSplunkUrlForVariant(variant, traceId, timestamp);

	if (!splunkUrl) {
		return null;
	}

	return (
		<Link href={splunkUrl} target="_blank" testId={testId}>
			{formatMessage(messages.ai_debugger_modal_splunk_link_label)}
		</Link>
	);
};
